import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { navigate } from '../../redux/navigator/action';
import { APP_ROUTES } from '../../types/global/routes.types';
import { userProfileSelector } from '../Dashboard/selectors';
import { userSubscriptionSelector } from '../EsSettings/selector';
import { toggleAccountSubscriptionModalSelector } from '../../selectors/selectors';
import {
    ensureObsidianDashboardFonts,
    OBSIDIAN,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
    dashboardShellSx,
} from '../../constants/obsidianDashboardTheme';
import {
    formatSandboxPrice,
    resolveSandboxMediaUrl,
    type SandboxCourse,
    type SandboxCourseRating,
} from '../../utils/sandboxCourseApi';
import { enrollSandboxFree, fetchSandboxCourse, fetchTeacherOfficeHours } from '../../redux/sandbox/actions';
import { buildSandboxCoursePayment, type SandboxCoursePaymentPayload } from '../../utils/openSandboxCourseCheckout';
import { SandboxCoursePaymentModal } from '../../components/Sandbox/SandboxCoursePaymentModal';
import { SandboxBookOfficeHoursModal } from '../../components/Sandbox/SandboxBookOfficeHoursModal';
import { SandboxCourseRatingPanel } from '../../components/Sandbox/SandboxCourseRatingPanel';
import { SandboxRichText } from '../../components/Sandbox/SandboxRichText';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { toggleAuthModal } from '../../redux/authentication/action';
import { toggleSubscriptionModal } from '../../redux/appConfigurator/action';
import { normalizeCreatorPlanClass, PRO_PASS_PLAN_CLASSES } from '../../utils/planEntitlements';
const SURFACE = '#0e0e0e';

type CoursePayload = {
    course: SandboxCourse;
    pricing: {
        price_cents: number;
        subscriber_discount_applied?: boolean;
        free?: boolean;
        requires_pro_pass?: boolean;
        original_currency?: string;
        transaction_currency?: string;
        rate?: number;
        converted_amount?: number;
        formatted_for_display?: string | null;
        defaulted_to_usd?: boolean;
        student_total_cents?: number;
        processor_fee_profile?: string;
        fee_preview?: import('../../utils/openSandboxCourseCheckout').SandboxFeePreview;
    };
    enrolled?: boolean;
    is_owner?: boolean;
    is_subscription_active?: boolean;
    my_rating?: SandboxCourseRating | null;
    progress?: {
        enrollment?: { progress_percent?: number; last_lesson?: string; certificate_code?: string };
        lessons?: any[];
    };
};

export function SandboxCoursePage() {
    const { courseId = '' } = useParams<{ courseId: string }>();
    const [searchParams] = useSearchParams();
    const fromStudio = searchParams.get('from') === 'studio';
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const user = useSelector(userProfileSelector);
    const subscription = useSelector(userSubscriptionSelector);
    const subscriptionModalOpen = useSelector(toggleAccountSubscriptionModalSelector);
    const [data, setData] = useState<CoursePayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [coverBroken, setCoverBroken] = useState(false);
    const [payment, setPayment] = useState<SandboxCoursePaymentPayload | null>(null);
    const [officeHoursOpen, setOfficeHoursOpen] = useState(false);
    const [officeHoursBookable, setOfficeHoursBookable] = useState(false);
    const [sectionTab, setSectionTab] = useState<'curriculum' | 'details'>('curriculum');
    const awaitingProPassSubscribe = useRef(false);

    useEffect(() => {
        ensureObsidianDashboardFonts();
    }, []);

    const load = useCallback(
        async (opts?: { soft?: boolean }) => {
            if (!courseId) return;
            if (!opts?.soft) setLoading(true);
            try {
                const res: any = await dispatchPromise(fetchSandboxCourse(courseId));
                setData(res?.data || res);
            } catch (e: any) {
                showToastMessage(e?.message || 'Course not found', 'error');
            } finally {
                if (!opts?.soft) setLoading(false);
            }
        },
        [courseId, dispatchPromise],
    );

    useEffect(() => {
        load();
    }, [load]);

    const openProPassModal = useCallback(() => {
        awaitingProPassSubscribe.current = true;
        dispatch(
            toggleSubscriptionModal({
                open: true,
                allowedPlanClasses: PRO_PASS_PLAN_CLASSES,
            }),
        );
    }, [dispatch]);

    // After Pro Pass subscribe modal closes, refresh course access without a full-page spinner.
    useEffect(() => {
        if (subscriptionModalOpen) return;
        if (!awaitingProPassSubscribe.current) return;
        awaitingProPassSubscribe.current = false;
        void load({ soft: true });
    }, [subscriptionModalOpen, load]);

    // If plan updates to Pro / Pro Plus while this course still requires Pro Pass, refresh.
    useEffect(() => {
        if (!data?.pricing?.requires_pro_pass) return;
        const planClass = normalizeCreatorPlanClass(
            subscription?.name || subscription?.plan_name || (subscription as any)?.plan?.name,
        );
        if (!planClass || !PRO_PASS_PLAN_CLASSES.includes(planClass)) return;
        void load({ soft: true });
    }, [subscription, data?.pricing?.requires_pro_pass, load]);

    const course = data?.course;
    const pricing = data?.pricing;
    const canWatch = Boolean(data?.enrolled || data?.is_owner);
    const coverUrl = resolveSandboxMediaUrl(course?.cover_image);
    const showCover = Boolean(coverUrl) && !coverBroken;
    const teacherId =
        typeof course?.teacher === 'object'
            ? course?.teacher?._id
            : (course as { teacher?: string } | undefined)?.teacher;
    const teacherName =
        typeof course?.teacher === 'object'
            ? [course.teacher?.firstName, course.teacher?.lastName].filter(Boolean).join(' ') ||
              course.teacher?.userName
            : undefined;

    useEffect(() => {
        if (!teacherId || !data?.enrolled) {
            setOfficeHoursBookable(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res: any = await dispatchPromise(fetchTeacherOfficeHours(String(teacherId)));
                if (!cancelled) setOfficeHoursBookable(Boolean(res?.bookable));
            } catch {
                if (!cancelled) setOfficeHoursBookable(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [teacherId, data?.enrolled, dispatchPromise]);

    useEffect(() => {
        setCoverBroken(false);
    }, [coverUrl]);

    const lessons = (course?.modules || []).flatMap(m => m.lessons || []);
    const resumeLessonId = data?.progress?.enrollment?.last_lesson || lessons[0]?._id;
    const completedLessonIds = new Set(
        (data?.progress?.lessons || []).filter((p: any) => p.completed).map((p: any) => String(p.lesson)),
    );
    const progressPercent = Number(data?.progress?.enrollment?.progress_percent) || 0;
    const hasStarted = progressPercent > 0 || Boolean(data?.progress?.enrollment?.last_lesson);

    const handleEnroll = async () => {
        if (!user?._id) {
            dispatch(toggleAuthModal(true));
            return;
        }
        if (!course || !pricing) return;
        if (pricing.requires_pro_pass) {
            openProPassModal();
            return;
        }
        setBusy(true);
        try {
            if (pricing.free) {
                await dispatchPromise(enrollSandboxFree(course._id));
                showToastMessage('You are enrolled — start watching anytime', 'success');
                await load();
            } else {
                const payload = buildSandboxCoursePayment({
                    course,
                    pricing,
                    conversion: {
                        original_currency: pricing.original_currency,
                        transaction_currency: pricing.transaction_currency,
                        rate: pricing.rate,
                        converted_amount: pricing.converted_amount,
                        formatted_for_display: pricing.formatted_for_display,
                        defaulted_to_usd: pricing.defaulted_to_usd,
                    },
                });
                if (payload) setPayment(payload);
            }
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not enroll', 'error');
        } finally {
            setBusy(false);
        }
    };

    const openLesson = (lessonId: string, isFreePreview?: boolean) => {
        if (!canWatch && !isFreePreview) {
            if (!user?._id) {
                dispatch(toggleAuthModal(true));
                return;
            }
            if (pricing?.requires_pro_pass) {
                showToastMessage('Pro or Pro Plus is required to unlock this class', 'info');
                openProPassModal();
                return;
            }
            showToastMessage(
                pricing?.free ? 'Enroll free to unlock this class' : 'Buy the course to unlock this class',
                'info',
            );
            handleEnroll();
            return;
        }
        dispatch(
            navigate({
                routes: APP_ROUTES.SANDBOX_PLAYER.replace(':courseId', courseId).replace(':lessonId', lessonId),
            }),
        );
    };

    if (loading) {
        return (
            <Box
                sx={{
                    ...dashboardShellSx,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: SURFACE,
                }}>
                <CircularProgress sx={{ color: OBSIDIAN.primary }} />
            </Box>
        );
    }

    if (!course) {
        return (
            <Box sx={{ ...dashboardShellSx, bgcolor: SURFACE, p: 3 }}>
                <Typography sx={{ color: OBSIDIAN.onSurfaceVariant }}>Course not found</Typography>
            </Box>
        );
    }

    const priceCents = Number(pricing?.price_cents ?? course.price_cents ?? 0);
    const studentCents = Number(pricing?.student_total_cents) > 0 ? Number(pricing?.student_total_cents) : priceCents;
    const priceLabel = pricing?.requires_pro_pass
        ? 'Pro Pass'
        : pricing?.free || priceCents <= 0
        ? 'Included'
        : pricing?.formatted_for_display &&
          !pricing.defaulted_to_usd &&
          pricing.transaction_currency &&
          pricing.transaction_currency !== 'usd'
        ? `${formatSandboxPrice(priceCents, course.currency)} · ${pricing.formatted_for_display}`
        : formatSandboxPrice(studentCents, course.currency);
    const showBookOfficeHours = Boolean(
        teacherId && data?.enrolled && (officeHoursBookable || course.office_hours_enabled),
    );

    const goBack = () => {
        if (fromStudio) {
            dispatch(navigate({ routes: APP_ROUTES.SANDBOX_STUDIO }));
            return;
        }
        dispatch(navigate({ routes: APP_ROUTES.SANDBOX }));
    };

    const openManageClasses = () => {
        dispatch(navigate({ routes: `${APP_ROUTES.SANDBOX_STUDIO}?manage=${courseId}` }));
    };

    return (
        <Box sx={{ ...dashboardShellSx, bgcolor: SURFACE, color: OBSIDIAN.onSurface, pb: 10 }}>
            {/* Hero */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: { xs: '16 / 10', md: '21 / 9' },
                    maxHeight: 360,
                    bgcolor: OBSIDIAN.surfaceContainer,
                    overflow: 'hidden',
                }}>
                {showCover ? (
                    <Box
                        component="img"
                        src={coverUrl!}
                        alt=""
                        onError={() => setCoverBroken(true)}
                        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `radial-gradient(ellipse at 70% 20%, rgba(255,201,101,0.12), transparent 55%), ${OBSIDIAN.surfaceContainer}`,
                        }}>
                        <SchoolIcon sx={{ fontSize: 56, color: OBSIDIAN.primary, opacity: 0.45 }} />
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, #0e0e0e 0%, rgba(14,14,14,0.55) 45%, transparent 100%)',
                    }}
                />
                <Box sx={{ position: 'absolute', top: 12, left: 16, zIndex: 1 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={goBack}
                        sx={{
                            color: OBSIDIAN.onSurface,
                            textTransform: 'none',
                            bgcolor: 'rgba(0,0,0,0.45)',
                            backdropFilter: 'blur(8px)',
                            borderRadius: 999,
                            px: 1.5,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                        }}>
                        {fromStudio ? 'Studio' : 'Academy'}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 3 }, mt: -4, position: 'relative', zIndex: 1, maxWidth: 960, mx: 'auto' }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: { xs: 24, md: 32 },
                        color: OBSIDIAN.onSurface,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                    }}>
                    {course.title}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 2.5 }}>
                    {(course.categories || []).map(c => (
                        <Chip
                            key={c}
                            label={c}
                            size="small"
                            sx={{
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                color: OBSIDIAN.primary,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontWeight: 600,
                            }}
                        />
                    ))}
                    {course.mastery_tier && (
                        <Chip
                            label={course.mastery_tier}
                            size="small"
                            sx={{
                                bgcolor: OBSIDIAN.surfaceContainerHigh,
                                color: OBSIDIAN.onSurfaceVariant,
                                textTransform: 'capitalize',
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}
                        />
                    )}
                    {Number(course.rating_count) > 0 ? (
                        <Chip
                            label={`★ ${Number(course.rating_avg || 0).toFixed(1)} (${course.rating_count})`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255,201,101,0.15)',
                                color: OBSIDIAN.primary,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontWeight: 600,
                            }}
                        />
                    ) : null}
                    {data?.is_subscription_active &&
                        course.with_subscriber_discount &&
                        course.pricing_mode !== 'pro_pass' &&
                        !course.is_free_for_subscriber &&
                        Number(course.price_cents) > 0 && (
                            <Chip
                                label="Subscriber pricing"
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(0,253,193,0.15)',
                                    color: OBSIDIAN.secondary,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    {data?.progress?.enrollment?.certificate_code && (
                        <Chip
                            label={`Cert ${data.progress.enrollment.certificate_code}`}
                            size="small"
                            sx={{ bgcolor: OBSIDIAN.surfaceContainerHigh, color: OBSIDIAN.primary }}
                        />
                    )}
                </Box>

                {/* CTA bar */}
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: OBSIDIAN.surfaceContainer,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3.5,
                        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 11,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            Course access
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                fontSize: 26,
                                color: OBSIDIAN.primary,
                                letterSpacing: '-0.02em',
                            }}>
                            {priceLabel}
                        </Typography>
                        {pricing?.subscriber_discount_applied &&
                            Number(course.price_cents) > Number(pricing.price_cents) && (
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurfaceVariant,
                                        textDecoration: 'line-through',
                                    }}>
                                    {formatSandboxPrice(course.price_cents, course.currency)}
                                </Typography>
                            )}
                        {typeof data?.progress?.enrollment?.progress_percent === 'number' && canWatch && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: OBSIDIAN.secondary,
                                    mt: 0.5,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                {data.progress.enrollment.progress_percent}% complete
                            </Typography>
                        )}
                    </Box>

                    {canWatch ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
                            {resumeLessonId ? (
                                <Button
                                    startIcon={<PlayCircleOutlineIcon />}
                                    onClick={() => openLesson(String(resumeLessonId))}
                                    sx={{
                                        px: 2.5,
                                        py: 1.25,
                                        borderRadius: 999,
                                        background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                        color: OBSIDIAN.onPrimaryFixed,
                                        fontWeight: 800,
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        textTransform: 'none',
                                        fontSize: 14,
                                        boxShadow: '0 6px 20px rgba(255,201,101,0.25)',
                                        '&:hover': { filter: 'brightness(1.05)' },
                                    }}>
                                    {hasStarted ? 'Resume' : 'Start watching'}
                                </Button>
                            ) : data?.is_owner ? (
                                <Button
                                    onClick={openManageClasses}
                                    sx={{
                                        px: 2.5,
                                        py: 1.25,
                                        borderRadius: 999,
                                        background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                        color: OBSIDIAN.onPrimaryFixed,
                                        fontWeight: 800,
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        textTransform: 'none',
                                    }}>
                                    Add classes
                                </Button>
                            ) : (
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: OBSIDIAN.onSurfaceVariant,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                    }}>
                                    Classes coming soon
                                </Typography>
                            )}
                            {showBookOfficeHours ? (
                                <Button
                                    startIcon={<ScheduleIcon />}
                                    onClick={() => setOfficeHoursOpen(true)}
                                    sx={{
                                        px: 2,
                                        py: 1.25,
                                        borderRadius: 999,
                                        border: `1px solid ${OBSIDIAN.primary}66`,
                                        color: OBSIDIAN.primary,
                                        fontWeight: 700,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        textTransform: 'none',
                                        fontSize: 13,
                                    }}>
                                    Book office hours
                                </Button>
                            ) : null}
                        </Box>
                    ) : (
                        <Button
                            disabled={busy}
                            onClick={handleEnroll}
                            sx={{
                                px: 2.5,
                                py: 1.25,
                                borderRadius: 999,
                                background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                color: OBSIDIAN.onPrimaryFixed,
                                fontWeight: 800,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                textTransform: 'none',
                                fontSize: 14,
                                boxShadow: '0 6px 20px rgba(255,201,101,0.25)',
                                '&.Mui-disabled': { opacity: 0.5, color: OBSIDIAN.onPrimaryFixed },
                            }}>
                            {busy
                                ? '…'
                                : !user?._id
                                ? 'Sign in to enroll'
                                : pricing?.requires_pro_pass
                                ? 'Get Pro Pass'
                                : pricing?.free
                                ? course.pricing_mode === 'pro_pass' || course.is_free_for_subscriber
                                    ? 'Enroll with Pro Pass'
                                    : 'Enroll free'
                                : 'Buy & enroll'}
                        </Button>
                    )}
                </Box>

                <SandboxCourseRatingPanel
                    courseId={course._id}
                    ratingAvg={course.rating_avg}
                    ratingCount={course.rating_count}
                    myRating={data?.my_rating || null}
                    canRate={Boolean(data?.enrolled && !data?.is_owner)}
                    onRated={({ rating_avg, rating_count, my_rating }) => {
                        setData(prev =>
                            prev
                                ? {
                                      ...prev,
                                      my_rating,
                                      course: {
                                          ...prev.course,
                                          rating_avg,
                                          rating_count,
                                      },
                                  }
                                : prev,
                        );
                    }}
                />

                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: '0.08em',
                        color: OBSIDIAN.onSurfaceVariant,
                        mb: 1.5,
                        textTransform: 'uppercase',
                    }}>
                    {sectionTab === 'details' ? 'Overview' : 'Curriculum'}
                </Typography>

                {course.description ? (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            p: 0.5,
                            mb: 2,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            gap: 0.5,
                        }}>
                        {(
                            [
                                { id: 'curriculum' as const, label: 'Curriculum' },
                                { id: 'details' as const, label: 'Details' },
                            ] as const
                        ).map(tab => {
                            const active = sectionTab === tab.id;
                            return (
                                <Button
                                    key={tab.id}
                                    onClick={() => setSectionTab(tab.id)}
                                    sx={{
                                        minWidth: 0,
                                        px: 2,
                                        py: 0.75,
                                        borderRadius: 999,
                                        textTransform: 'none',
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontWeight: 700,
                                        fontSize: 13,
                                        color: active ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                                        bgcolor: active ? OBSIDIAN.secondary : 'transparent',
                                        boxShadow: active ? '0 2px 10px rgba(0,253,193,0.25)' : 'none',
                                        '&:hover': {
                                            bgcolor: active ? OBSIDIAN.secondary : OBSIDIAN.surfaceContainerHigh,
                                        },
                                    }}>
                                    {tab.label}
                                </Button>
                            );
                        })}
                    </Box>
                ) : null}

                {sectionTab === 'details' && course.description ? (
                    <Box sx={{ mb: 2 }}>
                        <SandboxRichText
                            content={course.description}
                            fontSize={18}
                            fontWeight={700}
                            color={OBSIDIAN.onSurface}
                        />
                    </Box>
                ) : !course.modules?.length || lessons.length === 0 ? (
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            bgcolor: OBSIDIAN.surfaceContainer,
                            textAlign: 'center',
                        }}>
                        <Typography
                            sx={{
                                color: OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                mb: data?.is_owner ? 2 : 0,
                            }}>
                            No classes yet{data?.is_owner ? '. Add classes from Studio.' : '.'}
                        </Typography>
                        {data?.is_owner && (
                            <Button
                                onClick={openManageClasses}
                                sx={{
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                    color: OBSIDIAN.onPrimaryFixed,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                }}>
                                Add classes
                            </Button>
                        )}
                    </Box>
                ) : (
                    (course.modules || []).map(mod => (
                        <Box key={mod._id} sx={{ mb: 2.5 }}>
                            <Typography
                                sx={{
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    color: OBSIDIAN.secondary,
                                    mb: 1,
                                }}>
                                {mod.title}
                            </Typography>
                            {(mod.lessons || []).map(lesson => {
                                const locked = !canWatch && !(lesson as any).is_free_preview;
                                const done = completedLessonIds.has(String(lesson._id));
                                return (
                                    <Box
                                        key={lesson._id}
                                        onClick={() => openLesson(lesson._id, Boolean((lesson as any).is_free_preview))}
                                        sx={{
                                            p: 1.75,
                                            mb: 1,
                                            borderRadius: 2.5,
                                            bgcolor: OBSIDIAN.surfaceContainer,
                                            cursor: locked ? 'default' : 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            opacity: locked ? 0.65 : 1,
                                            transition: 'background 0.15s ease',
                                            border: done
                                                ? `1px solid ${OBSIDIAN.secondary}44`
                                                : '1px solid transparent',
                                            '&:hover': locked ? undefined : { bgcolor: OBSIDIAN.surfaceContainerHigh },
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 2,
                                                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: locked
                                                        ? OBSIDIAN.onSurfaceVariant
                                                        : done
                                                        ? OBSIDIAN.secondary
                                                        : OBSIDIAN.primary,
                                                    flexShrink: 0,
                                                }}>
                                                {locked ? (
                                                    <LockOutlinedIcon sx={{ fontSize: 18 }} />
                                                ) : done ? (
                                                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                                                ) : (
                                                    <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
                                                )}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        color: OBSIDIAN.onSurface,
                                                    }}
                                                    noWrap>
                                                    {lesson.title}
                                                </Typography>
                                                {lesson.description ? (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: OBSIDIAN.onSurfaceVariant,
                                                            fontFamily: OBSIDIAN_BODY_FONT,
                                                            mt: 0.25,
                                                            lineHeight: 1.45,
                                                        }}>
                                                        {lesson.description}
                                                    </Typography>
                                                ) : null}
                                                <Box sx={{ display: 'flex', gap: 1, mt: 0.35, flexWrap: 'wrap' }}>
                                                    {(lesson as any).is_free_preview && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 11,
                                                                color: OBSIDIAN.secondary,
                                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                            }}>
                                                            Free preview
                                                        </Typography>
                                                    )}
                                                    {lesson.has_assignment && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 11,
                                                                color: OBSIDIAN.primary,
                                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                            }}>
                                                            Includes project
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                        {lesson.duration_seconds ? (
                                            <Typography
                                                sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant, flexShrink: 0 }}>
                                                {Math.round(lesson.duration_seconds / 60)} min
                                            </Typography>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </Box>
                    ))
                )}
            </Box>

            {payment && course && (
                <SandboxCoursePaymentModal
                    open
                    title={`Enroll — ${course.title}`}
                    payload={payment}
                    onClose={() => setPayment(null)}
                    onSuccess={() => {
                        setPayment(null);
                        void load();
                    }}
                />
            )}
            {teacherId ? (
                <SandboxBookOfficeHoursModal
                    open={officeHoursOpen}
                    teacherId={String(teacherId)}
                    teacherName={teacherName}
                    onClose={() => setOfficeHoursOpen(false)}
                    bookBlockedHint={
                        data?.is_owner
                            ? "You're the instructor on this course, so you can't book your own office hours."
                            : undefined
                    }
                />
            ) : null}
        </Box>
    );
}
